import {
  List,
  Datagrid,
  TextField,
  NumberField,
  DateField,
  FunctionField,
  SelectField,
  SimpleList,
  TopToolbar,
  ShowButton,
} from "react-admin";
import { useMediaQuery } from "@mui/material";
import { EmptyList, EmptyWithAction } from "server/components";
import { MobileListRow } from "features/MobileListRow";
import { MOBILE_BREAKPOINT } from "utils/constants";
import { PublicationSourceChoices } from "utils/choices";
import { settings } from "app";
import { formatDateOnlyBg } from "server/formatting/formatDateBg";
import ImportPublicationsButton from "./ImportPublicationsButton";
import DoiLink from "./Partials/DoiLink";
import PublicationProfileSection from "./Profile/PublicationProfileSection";
import { isProfileReadyForImport, usePublicationProfile } from "./Profile/usePublicationProfile";

// Authors come unordered; `order` is their position in the publication's byline.
const formatAuthors = authors =>
  [...(authors ?? [])]
    .sort((a, b) => a.order - b.order)
    .map(author => author.fullName)
    .join(", ");

const SourceField = () => (
  <SelectField
    source="source"
    choices={PublicationSourceChoices}
  />
);

const ListActions = ({ canImport }) => (
  <TopToolbar>
    <ImportPublicationsButton canImport={canImport} />
  </TopToolbar>
);

// react-admin renders `empty` instead of the whole list (toolbar included), so the first import must be offered here.
const EmptyPublications = ({ canImport }) => (
  <EmptyWithAction direction="column">
    <ImportPublicationsButton
      canImport={canImport}
      align="center"
    />
  </EmptyWithAction>
);

// The endpoint returns the logged-in employee's publications, paginated ({ items, totalItems, ... }).
const ListEmployeePublications = () => {
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const { profile, fetchProfile } = usePublicationProfile();
  const canImport = isProfileReadyForImport(profile);

  return (
    <>
      <PublicationProfileSection
        profile={profile}
        onSaved={fetchProfile}
      />
      <List
        title="Публикации"
        exporter={false}
        actions={<ListActions canImport={canImport} />}
        empty={<EmptyPublications canImport={canImport} />}
        queryOptions={{ meta: { mixed_provider: true } }}
      >
        {isMobile ? (
          <SimpleList
            empty={<EmptyList />}
            linkType={false}
            sx={settings.style.mobileList}
            primaryText={record => (
              <div>
                <MobileListRow fields={[{ label: "Заглавие", value: record.title }]} />
                <MobileListRow fields={[{ label: "Автори", value: formatAuthors(record.authors) }]} />
                <MobileListRow fields={[{ label: "Година", value: record.year }]} />
              </div>
            )}
            secondaryText={record => (
              <div>
                <MobileListRow fields={[{ label: "DOI", value: <DoiLink doi={record.doi} /> }]} />
                <MobileListRow fields={[{ label: "Списание", value: record.journal }]} />
                <MobileListRow fields={[{ label: "Тип", value: record.documentType }]} />
                <MobileListRow fields={[{ label: "Издател", value: record.publisher }]} />
                <MobileListRow
                  fields={[{ label: "Дата на публикуване", value: formatDateOnlyBg(record.publicationDate) }]}
                />
                <MobileListRow fields={[{ label: "Цитирания", value: record.citationCount }]} />
                <MobileListRow fields={[{ label: "Източник", value: <SourceField /> }]} />
                <MobileListRow fields={[{ label: "Език", value: record.language }]} />
              </div>
            )}
            tertiaryText={() => <ShowButton label="" />}
          />
        ) : (
          <Datagrid
            bulkActionButtons={false}
            rowClick={false}
          >
            <TextField
              source="title"
              label="Заглавие"
              sortable={false}
            />
            <FunctionField
              label="Автори"
              render={record => formatAuthors(record.authors)}
            />
            <FunctionField
              label="DOI"
              render={record => <DoiLink doi={record.doi} />}
            />
            <TextField
              source="journal"
              label="Списание"
              sortable={false}
            />
            <TextField
              source="documentType"
              label="Тип"
              sortable={false}
            />
            <TextField
              source="publisher"
              label="Издател"
              sortable={false}
            />
            <NumberField
              source="year"
              label="Година"
              options={{ useGrouping: false }}
              textAlign="center"
              sortable={false}
            />
            <DateField
              source="publicationDate"
              label="Дата на публикуване"
              locales="bg-BG"
              sortable={false}
            />
            <NumberField
              source="citationCount"
              label="Цитирания"
              textAlign="center"
              sortable={false}
            />
            <SourceField label="Източник" />
            <TextField
              source="language"
              label="Език"
              sortable={false}
            />
            <ShowButton />
          </Datagrid>
        )}
      </List>
    </>
  );
};

export default ListEmployeePublications;
